// Auditoria — DATOS REALES: bitácora general de creaciones, ediciones y
// eliminaciones en toda la app (GET /auditoria). El backend ya filtra por
// alcance: un SuperAdmin ve todo; un administrador con alcance solo ve lo
// de sus cooperativas/federaciones/confederaciones.
//
// Solo visible para SuperAdmin y administradores (link en el menú, ver
// MainLayout); el backend igual lo exige con [Authorize] así que esta
// pantalla no es la única barrera.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Collapse, FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, History as HistoryIcon } from '@mui/icons-material';
import { auditoriaApi } from '../services/coopnetApi';
import { fecha } from '../utils/formato';

const ETIQUETA_ENTIDAD = {
  Comite: 'Comité', ComiteCargo: 'Cargo de comité', ComiteSubcomite: 'Subcomité',
  Afiliado: 'Afiliado', Cooperativa: 'Cooperativa', Federacion: 'Federación',
  Confederacion: 'Confederación', ConfederacionInternacional: 'Confederación internacional',
  UsuarioCoopNetRol: 'Rol asignado', Tercero: 'Tercero', Carnet: 'Carné', PagoInscripcion: 'Pago de inscripción',
  CooperativaDocumento: 'Documento de cooperativa',
};
const nombreEntidad = (e) => ETIQUETA_ENTIDAD[e] || e;

const ETIQUETA_ACCION = { crear: ['Creado', 'success'], actualizar: ['Editado', 'info'], eliminar: ['Eliminado', 'error'] };

function FilaCambios({ registro }) {
  const cambios = useMemo(() => {
    if (!registro.cambios) return [];
    try {
      return JSON.parse(registro.cambios);
    } catch {
      return [];
    }
  }, [registro.cambios]);

  const [abierto, setAbierto] = useState(false);
  const tieneDetalle = cambios.length > 0;
  const [textoAccion, colorAccion] = ETIQUETA_ACCION[registro.accion] || [registro.accion, 'default'];

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          {tieneDetalle && (
            <IconButton size="small" onClick={() => setAbierto((a) => !a)}>
              {abierto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{fecha(registro.creadoEn)}</TableCell>
        <TableCell>{registro.usuario || <Typography variant="caption" color="text.secondary">Anónimo</Typography>}</TableCell>
        <TableCell>
          <Chip size="small" color={colorAccion} label={textoAccion} />
        </TableCell>
        <TableCell>{nombreEntidad(registro.entidad)}</TableCell>
        <TableCell>
          {registro.cooperativa || registro.federacion || registro.confederacion || (
            <Typography variant="caption" color="text.secondary">—</Typography>
          )}
        </TableCell>
        <TableCell>
          {registro.sujeto || <Typography variant="caption" color="text.secondary">—</Typography>}
        </TableCell>
        <TableCell>{registro.resumen}</TableCell>
      </TableRow>
      {tieneDetalle && (
        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, borderBottom: abierto ? undefined : 'none' }}>
            <Collapse in={abierto} unmountOnExit>
              <Box sx={{ my: 1.5, pl: 5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campo</TableCell>
                      <TableCell>Antes</TableCell>
                      <TableCell>Después</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cambios.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{c.campo}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{String(c.anterior ?? '—')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{String(c.nuevo ?? '—')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const Auditoria = () => {
  const [filtro, setFiltro] = useState({ entidad: '', accion: '' });
  const [pagina, setPagina] = useState(0); // MUI: base 0
  const [tamano, setTamano] = useState(25);
  const [datos, setDatos] = useState({ total: 0, items: [] });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError('');
    auditoriaApi
      .listar({ ...filtro, pagina: pagina + 1, tamano })
      .then((r) => vivo && setDatos({ total: r.total ?? 0, items: Array.isArray(r.items) ? r.items : [] }))
      .catch((e) => vivo && setError(e.message || 'No se pudo cargar la auditoría.'))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [filtro, pagina, tamano]);

  const entidadesVistas = useMemo(
    () => [...new Set(datos.items.map((r) => r.entidad))].sort((a, b) => nombreEntidad(a).localeCompare(nombreEntidad(b))),
    [datos.items]
  );

  const cambiarFiltro = (parcial) => {
    setFiltro((f) => ({ ...f, ...parcial }));
    setPagina(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <HistoryIcon color="action" />
        <Typography variant="h4" fontWeight="bold">
          Auditoría
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Quién creó, editó o eliminó cada cosa — comités, cargos, cooperativas, afiliados, roles y más.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Tipo</InputLabel>
          <Select label="Tipo" value={filtro.entidad} onChange={(e) => cambiarFiltro({ entidad: e.target.value })}>
            <MenuItem value="">Todos</MenuItem>
            {entidadesVistas.map((en) => (
              <MenuItem key={en} value={en}>
                {nombreEntidad(en)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Acción</InputLabel>
          <Select label="Acción" value={filtro.accion} onChange={(e) => cambiarFiltro({ accion: e.target.value })}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="crear">Creado</MenuItem>
            <MenuItem value="actualizar">Editado</MenuItem>
            <MenuItem value="eliminar">Eliminado</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Desde"
          InputLabelProps={{ shrink: true }}
          value={filtro.desde || ''}
          onChange={(e) => cambiarFiltro({ desde: e.target.value || undefined })}
        />
        <TextField
          size="small"
          type="date"
          label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={filtro.hasta || ''}
          onChange={(e) => cambiarFiltro({ hasta: e.target.value || undefined })}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Fecha</TableCell>
              <TableCell>Quién</TableCell>
              <TableCell>Acción</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Dónde</TableCell>
              <TableCell>Para</TableCell>
              <TableCell>Qué cambió</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : datos.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay registros con este filtro.
                </TableCell>
              </TableRow>
            ) : (
              datos.items.map((r) => <FilaCambios key={r.id} registro={r} />)
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={datos.total}
          page={pagina}
          onPageChange={(_, p) => setPagina(p)}
          rowsPerPage={tamano}
          onRowsPerPageChange={(e) => {
            setTamano(parseInt(e.target.value, 10));
            setPagina(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default Auditoria;
